/**
 * @module components/auth/UserManagementPanel
 * @summary Super-admin panel: list, add, delete, and reset-password for users.
 * @description On mount, syncs the user list from the Worker API so changes
 *   made from any browser are reflected immediately.
 */
import { useState, useEffect } from 'react';
import {
  getUsers,
  addUser,
  deleteUser,
  resetPassword,
  getCurrentUser,
  syncUsersFromApi,
  ROLE_LABELS,
  ROLE_COLORS,
} from '../../lib/auth';
import type { Role, User } from '../../types';

interface Props {
  onClose: () => void;
}

export function UserManagementPanel({ onClose }: Props) {
  const [users, setUsers] = useState<User[]>(() => getUsers());
  const [uname, setUname] = useState('');
  const [pwd, setPwd] = useState('');
  const [role, setRole] = useState<Role>('engineer');
  const [err, setErr] = useState('');
  const [resetFor, setResetFor] = useState<string | null>(null);

  const me = getCurrentUser();
  const superCount = users.filter((u) => u.role === 'superadmin').length;
  const refresh = () => setUsers(getUsers());

  // Sync from API so all browsers see the same user list.
  useEffect(() => {
    syncUsersFromApi().then((synced) => {
      if (synced.length) setUsers(synced);
    });
  }, []);

  const handleAdd = async () => {
    const e = await addUser(uname, pwd, role);
    if (e) {
      setErr(e);
      return;
    }
    setUname('');
    setPwd('');
    setErr('');
    refresh();
  };

  return (
    <div className="overlay-panel open">
      <div className="asp-hdr um-hdr">
        <button type="button" className="asp-back-btn" onClick={onClose}>
          ← Back to Tool
        </button>
        <span className="asp-hdr-title">👥 User Management</span>
        <span className="asp-hdr-hint">Create accounts and assign roles to team members</span>
      </div>

      <div className="um-body">
        <div className="um-add-card">
          <div className="um-add-title">Add New User</div>
          <div className="um-row">
            <div className="um-fld">
              <label className="um-lbl">Username</label>
              <input className="um-inp" autoComplete="off" value={uname} onChange={(e) => setUname(e.target.value)} placeholder="e.g. john.doe" />
            </div>
            <div className="um-fld">
              <label className="um-lbl">Password</label>
              <input className="um-inp" type="password" autoComplete="off" value={pwd} onChange={(e) => setPwd(e.target.value)} placeholder="Password" />
            </div>
            <div className="um-fld um-fld-role">
              <label className="um-lbl" htmlFor="um-role-sel">Role</label>
              <select id="um-role-sel" className="um-sel" value={role} onChange={(e) => setRole(e.target.value as Role)}>
                <option value="engineer">Engineer</option>
                <option value="manager">Manager</option>
                <option value="superadmin">Super Admin</option>
              </select>
            </div>
            <button type="button" className="um-btn-add" onClick={handleAdd}>
              + Add User
            </button>
          </div>
          <div className="um-err">{err}</div>
        </div>

        <div className="um-section-title">All Users</div>
        <div>
          {users.map((u) => {
            const isMe = me?.username === u.username;
            const isLast = u.role === 'superadmin' && superCount === 1;
            return (
              <div className="um-user-row" key={u.username}>
                <div className="um-avatar" style={{ background: ROLE_COLORS[u.role] }}>
                  {(u.username[0] || '?').toUpperCase()}
                </div>
                <span className="um-uname">{u.username}</span>
                <span className={'um-role-badge ' + u.role}>{ROLE_LABELS[u.role]}</span>
                {isMe && <span className="um-you">(you)</span>}
                <button type="button" className="um-rpwd-btn" onClick={() => setResetFor(u.username)}>
                  🔑 Reset Pwd
                </button>
                <button
                  type="button"
                  className="um-del-btn"
                  disabled={isMe || isLast}
                  title={isMe ? 'Cannot delete yourself' : isLast ? 'Cannot delete the last Super Admin' : ''}
                  onClick={async () => {
                    if (!confirm(`Delete user "${u.username}"? This cannot be undone.`)) return;
                    const e = await deleteUser(u.username);
                    if (e) setErr(e);
                    refresh();
                  }}
                >
                  Delete
                </button>
              </div>
            );
          })}
          {!users.length && <div className="um-empty">No users found.</div>}
        </div>
      </div>

      {resetFor && (
        <ResetPasswordModal
          username={resetFor}
          onClose={() => setResetFor(null)}
          onSaved={() => { setResetFor(null); refresh(); }}
        />
      )}
    </div>
  );
}

function ResetPasswordModal({
  username,
  onClose,
  onSaved,
}: {
  username: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [nw, setNw] = useState('');
  const [cfm, setCfm] = useState('');
  const [err, setErr] = useState('');
  const save = async () => {
    if (nw !== cfm) { setErr('Passwords do not match.'); return; }
    const e = await resetPassword(username, nw);
    if (e) { setErr(e); return; }
    onSaved();
  };
  return (
    <div className="modal-overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal-box">
        <div className="sym-pwd-title">Reset Password</div>
        <div className="sym-pwd-sub">Resetting password for: {username}</div>
        <input type="password" placeholder="New password" autoFocus value={nw} onChange={(e) => setNw(e.target.value)} />
        <input type="password" placeholder="Confirm password" value={cfm} onChange={(e) => setCfm(e.target.value)} />
        <div className="sym-pwd-err">{err}</div>
        <div className="sym-pwd-row">
          <button type="button" className="sym-pwd-btn primary" onClick={save}>Save</button>
          <button type="button" className="sym-pwd-btn ghost" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
}
